import { List, Record } from 'immutable';
import { createContext } from 'react';

import { omit } from 'pkg/objects';
import { confirmNavigation } from 'pkg/router/utils';

export const BROWSER_PUSH = 'browser/PUSH' as const;
export const BROWSER_POP = 'browser/POP' as const;
export const BROWSER_REPLACE = 'browser/REPLACE' as const;
export const BROWSER_RESET = 'browser/RESET' as const;
export const BROWSER_TRAVERSE = 'browser/TRAVERSE' as const;

export type ActionType =
	| typeof BROWSER_PUSH
	| typeof BROWSER_POP
	| typeof BROWSER_REPLACE
	| typeof BROWSER_RESET
	| typeof BROWSER_TRAVERSE;

export interface RouterStateProps {
	type: ActionType | null;
	location: string;
	direction: number;
	position: number;
	state: { [key: string]: unknown };
	search: { [key: string]: unknown };
}

export interface RouterAction {
	type: ActionType;
	payload?: {
		location?: string;
		state?: { [key: string]: unknown };
		search?: { [key: string]: unknown };
		position?: number;
		fullReset?: boolean;
	};
	skipMutations?: boolean;
}

export interface RouterContextValue {
	store: RouterRecordType;
	dispatch: (action: RouterAction) => void;
	stateType: string;
}

export const RouterContext = createContext<RouterContextValue>(null!);

export const RouterState = Record<RouterStateProps>(
	{
		type: null,
		location: '/',
		direction: 0,
		position: 0,
		state: {},
		search: {},
	},
	'RouterState'
);

export type RouterStateType = ReturnType<typeof RouterState>;

interface RouterRecordProps {
	history: List<RouterStateType>;
}

const RouterRecord = Record<RouterRecordProps>(
	{
		history: List<RouterStateType>(),
	},
	'RouterRecord'
);

export type RouterRecordType = ReturnType<typeof RouterRecord>;

export const initialState: RouterRecordType = new RouterRecord({
	history: List([new RouterState()]),
});

export const initializeState =
	(initialHistory: List<RouterStateType> | RouterStateProps[] | null = null) =>
	(): RouterRecordType => {
		if (List.isList(initialHistory)) {
			return new RouterRecord({
				history: initialHistory,
			});
		} else if (Array.isArray(initialHistory)) {
			return new RouterRecord({
				history: List(initialHistory.map((item) => new RouterState(item))),
			});
		} else {
			return initialState;
		}
	};

/**
 *	Actions that have already moved `window.history`. React runs a reducer
 *	more than once for the same action: StrictMode does it on every update in
 *	development, which pushed each page twice and made Back take two taps.
 */
const mutated = new WeakSet<RouterAction>();

/**
 *	Where the router's current entry sits in `window.history`. Each entry the
 *	router writes carries its position, so a traversal the browser makes on
 *	its own (a back swipe, the browser's buttons) says where it landed.
 */
const currentPosition = (state: RouterRecordType): number =>
	state.get('history').last()?.get('position') ?? 0;

/** Whether this pass of `action` is the one that moves `window.history`. */
const shouldMutate = (action: RouterAction): boolean => {
	if (action.skipMutations || mutated.has(action)) {
		return false;
	}

	mutated.add(action);

	return true;
};

export const browserReducer = (
	state: RouterRecordType,
	action: RouterAction
): RouterRecordType => {
	const { type, payload } = action;

	const shouldUpdate = (nextLocation: string): boolean => {
		const history = state.get('history');

		return (
			history.size === 0 || history?.last()?.get('location') !== nextLocation
		);
	};

	if (!payload || shouldUpdate(payload.location || '')) {
		const didConfirm = confirmNavigation(() => {
			window.history.forward();
		});

		if (!didConfirm) {
			return state;
		}
	}

	if (!type) return state;

	switch (type) {
		case BROWSER_PUSH: {
			if (payload && shouldUpdate(payload.location || '')) {
				const nextState = new RouterState({
					...payload,
					type,
					direction: 1,
					position: currentPosition(state) + 1,
				});

				state = state.updateIn(['history'], (history: List<RouterStateType>) =>
					history.push(nextState)
				);

				if (shouldMutate(action)) {
					const historyState = omit(nextState.toJS(), 'state');

					window.history.pushState(
						{ ...historyState, type },
						'',
						nextState.location
					);
				}
			}

			return state;
		}

		case BROWSER_POP: {
			state = state.updateIn(['history'], (history: List<RouterStateType>) =>
				history.pop()
			);

			if (shouldMutate(action)) {
				window.history.back();
			}

			const historySize = state.get('history').size;

			if (historySize > 0) {
				state = state.setIn(['history', historySize - 1, 'direction'], -1);
			}

			return state;
		}

		case BROWSER_REPLACE: {
			if (payload && shouldUpdate(payload.location || '')) {
				const nextState = new RouterState({
					...payload,
					type,
					direction: 0,
					position: currentPosition(state),
				});

				state = state.updateIn(['history'], (history: List<RouterStateType>) =>
					history.splice(-1, 1, nextState)
				);

				if (shouldMutate(action)) {
					const historyState = omit(nextState.toJS(), 'state');

					window.history.replaceState(
						{ ...historyState, type },
						'',
						nextState.location
					);
				}
			}

			return state;
		}

		// The browser moved through its own history and already shows the entry,
		// so the router only catches up. It drops every entry from the landing
		// position on, so the one before is the real previous page again. An
		// entry the router never stamped only replaces the current one.
		case BROWSER_TRAVERSE: {
			const position = payload?.position;
			const nextState = new RouterState({
				location: payload?.location || '/',
				type,
				direction: 0,
				position: position ?? currentPosition(state),
			});

			return state.updateIn(['history'], (history: List<RouterStateType>) =>
				position === undefined
					? history.splice(-1, 1, nextState)
					: history
							.filter((entry) => entry.get('position') < position)
							.push(nextState)
			);
		}

		case BROWSER_RESET: {
			if (payload?.fullReset) {
				return initialState;
			}

			const last = state.get('history').last();

			if (last) {
				state = state.set('history', List([last]));
			}

			return state;
		}

		default:
			throw new Error(`No such action type: ${type}.`);
	}
};
